import type { CrossValidationResult, ReliabilityLevel } from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface CrossValidationResultCardProps {
  crossValidation: CrossValidationResult
  colorConfig?: ColorConfig
}

// 默认配色
const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

export function CrossValidationResultCard({ crossValidation, colorConfig = DEFAULT_COLOR }: CrossValidationResultCardProps) {
  const color = colorConfig
  const rate = Math.round(crossValidation.rate)

  const getReliabilityInfo = (level: ReliabilityLevel) => ({
    reliable: { text: '可靠', desc: '你的作答一致性高，结果可信度良好。' },
    attention_needed: { text: '需关注', desc: '部分维度在不同题目中的表现有差异，结果供参考。' },
    review_suggested: { text: '建议复核', desc: '作答一致性较低，建议重新测试或结合实际情况理解结果。' },
  }[level])

  const reliabilityInfo = getReliabilityInfo(crossValidation.reliable)

  // 计算环形进度
  const circumference = 2 * Math.PI * 40
  const strokeDashoffset = circumference - (rate / 100) * circumference

  return (
    <div
      className="bg-white rounded-xl p-6"
      style={{ border: `1px solid ${color.primary}15` }}
    >
      <h2 className="text-base font-semibold text-gray-900 mb-4">结果可靠性检验</h2>

      <div className="flex items-center gap-6">
        {/* 环形进度 */}
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-full h-full transform -rotate-90">
            {/* 背景圆 */}
            <circle
              cx="56"
              cy="56"
              r="40"
              stroke={`${color.primary}20`}
              strokeWidth="8"
              fill="none"
            />
            {/* 进度圆 */}
            <circle
              cx="56"
              cy="56"
              r="40"
              stroke={color.primary}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: color.primary }}>{rate}%</div>
              <div className="text-xs" style={{ color: `${color.primary}80` }}>一致率</div>
            </div>
          </div>
        </div>

        {/* 状态描述 */}
        <div className="flex-1">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3"
            style={{ backgroundColor: `${color.primary}10` }}
          >
            <span className="font-medium" style={{ color: color.primary }}>{reliabilityInfo.text}</span>
          </div>
          <p className="text-sm text-gray-600">{reliabilityInfo.desc}</p>

          {/* 详细统计 */}
          <div className="flex gap-4 mt-3 text-xs text-gray-500">
            <span>总检验点: {crossValidation.summary.totalChecks}</span>
            <span style={{ color: color.primary }}>一致: {crossValidation.summary.consistentChecks}</span>
            <span style={{ color: `${color.primary}60` }}>不一致: {crossValidation.summary.inconsistentChecks}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
